import { useEffect, useMemo, useState } from 'react';
import { fetchSuperadminOrganizationById } from '../../features/superadmin/api/organizations.api';
import { useAuth } from '../auth/use-auth';
import { canFetchSuperadminOrganizationSnapshotForDisplay } from '../access/access';
import {
  isLegacyNumericOrgPlaceholder,
  orgDisplayNameFromMe,
  orgNeutralLabel,
  orgNeutralTitle
} from '../lib/org-display-name';

type Options = {
  /** Уже известное имя (например из списка лукапа) — не дергать платформу. */
  preferName?: string | null;
};

/**
 * Человекочитаемое имя организации для UI: сначала me, затем (для платформы) снимок superadmin.
 * Не подставляет «Организация #id» как основной текст.
 */
export function useOrgDisplayName(
  organizationId: number | null | undefined,
  options?: Options
): { displayName: string; title?: string; loading: boolean } {
  const { me, apiClient } = useAuth();
  const preferRaw = (options?.preferName ?? '').trim();
  const prefer =
    preferRaw && !isLegacyNumericOrgPlaceholder(preferRaw) ? preferRaw : '';
  const [fetched, setFetched] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fromMe = useMemo(() => {
    if (organizationId == null || organizationId <= 0 || !me) return null;
    return orgDisplayNameFromMe(me, organizationId);
  }, [me, organizationId]);

  const canPlatformResolve =
    me != null &&
    organizationId != null &&
    organizationId > 0 &&
    canFetchSuperadminOrganizationSnapshotForDisplay(me);

  useEffect(() => {
    if (organizationId == null || organizationId <= 0) {
      setFetched(null);
      setLoading(false);
      return;
    }
    if (prefer) {
      setFetched(null);
      setLoading(false);
      return;
    }
    if (fromMe) {
      setFetched(null);
      setLoading(false);
      return;
    }
    if (!canPlatformResolve) {
      setFetched(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const ac = new AbortController();
    void fetchSuperadminOrganizationById(apiClient, organizationId, { signal: ac.signal })
      .then((snap) => {
        const n = (snap?.name ?? '').trim();
        const s = (snap?.slug ?? '').trim();
        setFetched(n || s || null);
      })
      .catch(() => setFetched(null))
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [apiClient, organizationId, prefer, fromMe, canPlatformResolve]);

  const resolved = prefer || fromMe || fetched;
  if (resolved) {
    return { displayName: resolved, title: undefined, loading: false };
  }
  if (loading) {
    return { displayName: 'Загрузка…', title: undefined, loading: true };
  }
  if (organizationId != null && organizationId > 0) {
    return {
      displayName: orgNeutralLabel(organizationId),
      title: orgNeutralTitle(organizationId),
      loading: false
    };
  }
  return { displayName: '—', loading: false };
}
